import { Dialog, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { SettingsService, TaskService } from "../../../services/api";
import FormInput from "../../molecules/FormInput";
import useRefresh from "../../../hooks/useRefresh";
import FormTemplate from "../../templates/FormTemplate";
import { useTranslation } from "react-i18next";
import { DropDownItem } from "../../molecules/Dropdown";
import { useMutation } from "@tanstack/react-query";
import useDidMountEffect from "../../../hooks/useDidMountEffect";

interface CreateTaskDialogProps {
  open: boolean;
  ticketId?: string;
  onSubmit?: () => void;
  onClose?: () => void;
}

export default function CreateTaskDialog(props: CreateTaskDialogProps) {
  const { t } = useTranslation("Ticket");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [taskTypes, setTaskTypes] = useState<DropDownItem[]>([]);
  const [taskType, setTaskType] = useState("");
  const refresh = useRefresh();

  const { mutate: loadTaskTypes, isPending: isLoadingTaskTypes } = useMutation({
    mutationFn: async () => {
      const res = await SettingsService.getTaskTypes();
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) setTaskTypes(data.map((e) => ({ name: e, value: e })));
    },
  });

  useDidMountEffect(() => {
    if (taskTypes.length > 0) setTaskType(taskTypes[0].value as string);
  }, [taskTypes]);

  useEffect(() => {
    setTitle("");
    setDescription("");
    loadTaskTypes();
  }, []);

  const handleSubmit = async () => {
    if (!props.ticketId) return;
    const res = await TaskService.createTask({
      ticketId: props.ticketId,
      createTaskBody: {
        title,
        description,
        taskType,
      },
    });

    if (res?.data?.success) {
      refresh();
      props.onClose?.();
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <FormTemplate
        title={t("CreateTask")}
        onSubmit={handleSubmit}
        onCancel={props.onClose}
      >
        <Stack spacing={2}>
          <FormInput
            label={t("Title")}
            value={title}
            placeholder="ex. Rearrange Warehouse"
            required
            field="title"
            type="string"
            onChange={(v) => setTitle(v as string)}
          />
          <FormInput
            label={t("Description")}
            field="description"
            type="string"
            value={description}
            placeholder={t("DescriptionPlaceholder")}
            required
            multiline
            rows={4}
            onChange={(v) => setDescription(v as string)}
          />
          <FormInput
            field="tasktype"
            label={t("TaskType")}
            type="list"
            onChange={(v) => setTaskType(v as string)}
            value={taskType}
            list={taskTypes}
            required
            loading={isLoadingTaskTypes}
          />
        </Stack>
      </FormTemplate>
    </Dialog>
  );
}
